
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n/useTranslation';
import { SparklesIcon, CalendarIcon, CheckCircleIcon, ArrowRightIcon } from '../components/icons';
import { AppPage } from '../types';

interface ServiceSelectionWizardPageProps {
    onNavigate: (page: AppPage, params?: Record<string, string>) => void;
}

const ServiceSelectionWizardPage: React.FC<ServiceSelectionWizardPageProps> = ({ onNavigate }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleSelection = (choice: 'membership' | 'consultation') => {
        if (choice === 'membership') {
            onNavigate('services');
        } else {
            onNavigate('booking');
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                delayChildren: 0.3
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1 }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center p-6 sm:p-12 relative overflow-hidden">
            {/* Background Decorative Elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-indigo-500/10 rounded-full blur-3xl"></div>
                <div className="absolute top-[40%] -right-[10%] w-[40%] h-[40%] bg-purple-500/10 rounded-full blur-3xl"></div>
            </div>

            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="max-w-4xl w-full z-10"
            >
                <motion.div variants={itemVariants} className="text-center mb-12">
                    <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 dark:text-white mb-4">
                        {t('wizard_title')}
                    </h1>
                    <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                        {t('wizard_subtitle')}
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                    {/* Membership Card */}
                    <motion.div
                        variants={itemVariants}
                        whileHover={{ y: -8, transition: { duration: 0.3 } }}
                        className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-100 dark:border-gray-700 hover:border-indigo-500 dark:hover:border-indigo-400 transition-colors cursor-pointer group"
                        onClick={() => handleSelection('membership')}
                    >
                        <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                            <SparklesIcon className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                            {t('wizard_membership_title')}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                            {t('wizard_membership_desc')}
                        </p>
                        <ul className="space-y-3 mb-8">
                            <li className="flex items-center text-gray-700 dark:text-gray-300">
                                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                                <span>{t('wizard_membership_feature_1')}</span>
                            </li>
                            <li className="flex items-center text-gray-700 dark:text-gray-300">
                                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                                <span>{t('wizard_membership_feature_2')}</span>
                            </li>
                            <li className="flex items-center text-gray-700 dark:text-gray-300">
                                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                                <span>{t('wizard_membership_feature_3')}</span>
                            </li>
                        </ul>
                        <button className="w-full py-3 px-6 rounded-xl bg-indigo-600 text-white font-semibold flex items-center justify-center gap-2 group-hover:bg-indigo-700 transition-colors">
                            {t('wizard_membership_cta')} <ArrowRightIcon className="w-4 h-4" />
                        </button>
                    </motion.div>

                    {/* Consultation Card */}
                    <motion.div
                        variants={itemVariants}
                        whileHover={{ y: -8, transition: { duration: 0.3 } }}
                        className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-100 dark:border-gray-700 hover:border-amber-500 dark:hover:border-amber-400 transition-colors cursor-pointer group"
                        onClick={() => handleSelection('consultation')}
                    >
                        <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                            <CalendarIcon className="w-8 h-8 text-amber-600 dark:text-amber-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                            {t('wizard_consultation_title')}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                            {t('wizard_consultation_desc')}
                        </p>
                        <ul className="space-y-3 mb-8">
                            <li className="flex items-center text-gray-700 dark:text-gray-300">
                                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                                <span>{t('wizard_consultation_feature_1')}</span>
                            </li>
                            <li className="flex items-center text-gray-700 dark:text-gray-300">
                                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                                <span>{t('wizard_consultation_feature_2')}</span>
                            </li>
                            <li className="flex items-center text-gray-700 dark:text-gray-300">
                                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                                <span>{t('wizard_consultation_feature_3')}</span>
                            </li>
                        </ul>
                        <button className="w-full py-3 px-6 rounded-xl bg-amber-500 text-white font-semibold flex items-center justify-center gap-2 group-hover:bg-amber-600 transition-colors">
                            {t('wizard_consultation_cta')} <ArrowRightIcon className="w-4 h-4" />
                        </button>
                    </motion.div>
                </div>

                <motion.div variants={itemVariants} className="text-center">
                    <button
                        onClick={() => onNavigate('home')}
                        className="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white font-medium transition-colors"
                    >
                        {t('wizard_skip')}
                    </button>
                </motion.div>

            </motion.div>
        </div>
    );
};

export default ServiceSelectionWizardPage;
